/**
 * THE identity-activity vocabulary — one action, one spelling, one owner.
 *
 * Foundations 2 (Users rail), B2e-T1.
 *
 * WHAT WENT WRONG, AND WHY IT WAS INVISIBLE
 * =========================================
 * The Activity feed reads `audit_logs` — the right store — and filters it with
 * a whitelist of `action` strings. The emitters wrote their own strings. When
 * the two disagreed the feed did not error, did not warn and did not render an
 * "unknown action" row: the `IN (...)` predicate simply matched nothing, so a
 * real event that happened to a real person was silently absent from the page
 * that exists to show it. Four pairs of spellings for one action were live at
 * once, and an activity TYPE (`session_terminated`) named an action no emitter
 * ever wrote, which is dead code that looks alive.
 *
 * THE FIX IS STRUCTURAL, NOT A RENAME
 * ===================================
 * This file is the one map. Everything downstream is DERIVED from it:
 *
 *   - `USER_ACTIVITY_ACTIONS`        the feed's `action IN (...)` whitelist
 *   - `USER_ACTIVITY_ACTION_TO_TYPE` the audit row -> UI verb projection
 *   - `USER_ACTIVITY_TYPE_TO_ACTIONS` the type filter, by INVERTING the map
 *   - `USER_ACTIVITY_FILTER_TYPES`   the type enum the API and the page offer
 *
 * Because the type filter is an inversion rather than a second table, a type
 * CANNOT name an action the whitelist omits — the shape that hid
 * `SESSION_TERMINATED` in plain sight for three waves is not expressible here.
 *
 * WHAT STOPS THE SPELLINGS DRIFTING AGAIN
 * =======================================
 * `packages/api/src/services/__tests__/activity-action-vocabulary.test.ts`
 * scans `packages/api/src` and fails when
 *   - an emitter writes an identity action this map does not carry,
 *   - this map carries an action NO emitter writes (a dead vocabulary entry),
 *   - one action appears in two spellings anywhere in the API source.
 * The scan is case- and separator-sensitive on purpose: `PASSWORD_CHANGED` and
 * `user.password_changed` are two names for one thing, and naming one thing
 * twice is the duplicate the one rule exists to stop.
 *
 * WHY UPPER_SNAKE AND NOT THE DOTTED ADMIN CONVENTION
 * ===================================================
 * `audit_logs.action` already holds both conventions: the Wave 3 admin
 * taxonomy is dotted (`webhook.created`) and the Wave 1a identity events are
 * UPPER_SNAKE (`LOGIN_SUCCESS`). The identity rows are the ones already on
 * disk in every deployment, and decisions.md 6 (2026-09-08) forbids rewriting
 * historical action strings — the audit log is hash-chained, and tidying a
 * chained row's action breaks the chain that proves nobody rewrote it. So the
 * identity vocabulary keeps the spelling its own history is written in, and
 * the emitters that had wandered off it are the ones that move.
 *
 * Registered, and enforced, in
 * `packages/api/src/services/audit-event-types.ts` (`IDENTITY_AUDIT_EVENTS`),
 * which is derived from this array rather than restating it.
 */

import { z } from 'zod';

/**
 * One entry = one audit action = one row class on the Activity feed.
 *
 * `type` is the UI verb the row renders as; several actions may share one
 * (`PASSWORD_CHANGED` and `PASSWORD_RESET` are both "password changed" to a
 * reader). `category` is the `audit_logs.category` the emitter writes, carried
 * here so the audit registry can be derived instead of restated.
 */
/**
 * The `audit_logs.category` values an identity-activity row may be filed
 * under. The audit browser filters on this column by equality, so an action
 * that is written under two of them is an action half the queries cannot see.
 */
export type IdentityAuditCategory =
  | 'authentication'
  | 'user_management'
  | 'security';

export interface UserActivityActionDefinition {
  /** The `audit_logs.action` string. THE spelling — there is no other. */
  readonly action: string;
  /** The UI verb this action renders and filters as. */
  readonly type: string;
  /**
   * THE `audit_logs.category` this action is filed under.
   *
   * B2e-T1 review (BUG-03 / R3): this field used to be a DESCRIPTION of what
   * the emitters happened to write, and five of thirteen entries described it
   * wrongly — `PASSWORD_CHANGED` was declared `security` and written
   * `user_management` by `routes/v1/users.ts` and `authentication` by
   * `routes/v1/auth.ts`, two categories for one action. An auditor filtering
   * the audit browser to `category=security` (an equality predicate) saw
   * neither. A second copy of a value is a second answer to one question, so
   * the emitters now READ this field through
   * `resolveIdentityAuditCategory()` rather than restating it: the
   * declaration is true by construction, and there is nothing left to drift.
   */
  readonly category: IdentityAuditCategory;
  /** What the row means to an auditor. Surfaced by the audit registry. */
  readonly description: string;
}

/**
 * THE VOCABULARY.
 *
 * Every entry has a live emitter in `packages/api/src` — asserted by the
 * vocabulary test, which is what keeps this list from growing entries the feed
 * can never match. Four names were removed here for exactly that reason:
 *
 *   MFA_CHALLENGE_SUCCESS, MFA_CHALLENGE_FAILED, MFA_ENROLLED, MFA_DISABLED
 *
 * They were carried as "// future MFA pipeline" and offered to the operator as
 * three filter types and a stat card. There is no MFA challenge and no MFA
 * enrolment in this product: `POST /auth/login` answers `requiresMfa: false`
 * unconditionally (routes/v1/auth.ts), no route ever sets `users.mfa_enabled`
 * to true, and the only writer of `mfa_secret` is the admin RESET that clears
 * it. A filter that can never match and a card that can only ever read zero
 * are not "ahead of the emitter"; they are a promise the page makes and cannot
 * keep. When MFA is built, its events are added here, with their emitters, in
 * the same change — which is the only order this list allows.
 *
 * `security.mfa_changed` is deliberately NOT here. It is the tenant's MFA
 * POLICY being edited (routes/v1/admin/security/auth.ts `PUT /mfa-config`,
 * a sibling of `security.password_policy_changed`), not an MFA event that
 * happened to a person — a different noun, correctly spelt in the dotted admin
 * taxonomy, and folding it in would put a tenant-configuration row on a
 * personal activity feed.
 */
const VOCABULARY = [
  // ── Signing in ────────────────────────────────────────────────────────────
  {
    action: 'LOGIN_SUCCESS',
    type: 'login',
    category: 'authentication',
    description:
      'A person signed in successfully. Written by POST /auth/login once the '
      + 'credential, the account state and the tenant context have all been '
      + 'accepted. This row IS the Logins (24h) number — the card counts these '
      + 'rows, it does not keep a counter of its own.',
  },
  {
    action: 'LOGIN_FAILED',
    type: 'login_failed',
    category: 'authentication',
    description:
      'A sign-in attempt was refused — wrong password, unknown email, locked, '
      + 'suspended, deactivated, no live membership, or a rate limit. Rows '
      + 'whose email resolved to no user carry a null user_id and are dropped '
      + 'from the tenant feed because they cannot be attributed.',
  },
  {
    action: 'LOGIN_TENANT_CONTEXT_SELECTED',
    type: 'login_context_selected',
    category: 'authentication',
    description:
      'A person who belongs to more than one tenant chose which one to sign '
      + 'in to, exchanging their context-selection token for a session. The '
      + 'row names the tenant that was entered, so "which door did they come '
      + 'through" is answerable after the fact.',
  },
  {
    action: 'LOGOUT',
    type: 'logout',
    category: 'authentication',
    description: 'A person signed out and their refresh session was ended.',
  },
  {
    action: 'TOKEN_REFRESH',
    type: 'token_refresh',
    category: 'authentication',
    description:
      'A refresh token was exchanged for a new access token. High-volume and '
      + 'collapsed into hourly groups by the feed, but kept because a refresh '
      + 'from an unexpected address is how a stolen token shows up.',
  },
  {
    action: 'TOKEN_ROTATION_VIOLATION',
    type: 'token_rotation_violation',
    category: 'security',
    description:
      'A refresh token was presented twice — the replay that means the token '
      + 'has been copied. The whole session family is revoked and this row is '
      + 'the evidence.',
  },

  // ── Passwords ─────────────────────────────────────────────────────────────
  {
    action: 'PASSWORD_CHANGED',
    type: 'password_changed',
    category: 'security',
    description:
      'A person changed their own password, whether they chose to or were '
      + 'made to by a forced reset.',
  },
  {
    action: 'PASSWORD_RESET',
    type: 'password_reset',
    category: 'security',
    description:
      'An administrator reset somebody else\'s password to a temporary one '
      + 'and revoked their sessions.',
  },
  {
    action: 'PASSWORD_RESET_FORCED',
    type: 'password_reset',
    category: 'security',
    description:
      'An administrator required a person to change their password at next '
      + 'sign-in. The person keeps their credential; they cannot spend it '
      + 'again until they replace it.',
  },

  // ── MFA, as it actually exists today ──────────────────────────────────────
  {
    action: 'MFA_RESET',
    type: 'mfa_reset',
    category: 'user_management',
    description:
      'An administrator cleared a person\'s MFA enrolment and revoked their '
      + 'sessions — the "they lost their phone" action. Written per person, '
      + 'whether the administrator acted on one or on a batch.',
  },

  // ── Impersonation ─────────────────────────────────────────────────────────
  {
    action: 'IMPERSONATION_STARTED',
    type: 'impersonation_started',
    category: 'authentication',
    description:
      'Somebody entered another person\'s account. The row is written AS THE '
      + 'IMPERSONATOR (B2d-T2): the actor is who is really there, never the '
      + 'person being impersonated.',
  },
  {
    action: 'IMPERSONATION_ENDED',
    type: 'impersonation_ended',
    category: 'authentication',
    description:
      'The impersonator ended their own session and returned to their own '
      + 'account.',
  },
  {
    action: 'IMPERSONATION_FORCE_ENDED',
    type: 'impersonation_ended',
    category: 'security',
    description:
      'Somebody OTHER than the impersonator ended an impersonation session — '
      + 'a tenant administrator through the Users rail, or a platform '
      + 'administrator through Admin > Security > Privileged. Both doors '
      + 'write this one action; they used to write two different strings for '
      + 'the same act.',
  },

  // ── Sessions ──────────────────────────────────────────────────────────────
  {
    action: 'SESSION_REVOKED',
    type: 'session_revoked',
    category: 'security',
    description:
      'One sign-in session was revoked — by the person from their own device '
      + 'list, or by an administrator from theirs.',
  },
] as const satisfies readonly UserActivityActionDefinition[];

/** THE vocabulary, as data. Derive from this; never restate it. */
export const USER_ACTIVITY_VOCABULARY: readonly UserActivityActionDefinition[] =
  VOCABULARY;

/** Every `audit_logs.action` the Activity surfaces know about. */
export type UserActivityActionName = (typeof VOCABULARY)[number]['action'];

/** Every UI verb an activity row can render and be filtered by. */
export type UserActivityFilterType = (typeof VOCABULARY)[number]['type'];

/**
 * What a rendered row's `type` may be.
 *
 * `other` is the projection's fallback for a row that is in the whitelist by
 * action but whose type the reader does not know — unreachable while the
 * projection derives from this map, and kept so a row read from a database
 * written by a NEWER build renders as a row rather than crashing the page.
 */
export type UserActivityEventType = UserActivityFilterType | 'other';

/**
 * The feed's `action IN (...)` predicate. Frozen: a caller that pushed onto it
 * would widen what the feed shows without widening what the vocabulary
 * declares, which is the drift this file exists to end.
 */
export const USER_ACTIVITY_ACTIONS: readonly UserActivityActionName[] =
  Object.freeze(VOCABULARY.map((v) => v.action)) as readonly UserActivityActionName[];

/** Audit row -> UI verb. */
export const USER_ACTIVITY_ACTION_TO_TYPE: Readonly<
  Record<string, UserActivityFilterType>
> = Object.freeze(
  Object.fromEntries(VOCABULARY.map((v) => [v.action, v.type])) as Record<
    string,
    UserActivityFilterType
  >
);

/**
 * Audit row -> the category it is filed under. Derived, never restated.
 */
export const USER_ACTIVITY_ACTION_TO_CATEGORY: Readonly<
  Record<string, IdentityAuditCategory>
> = Object.freeze(
  Object.fromEntries(VOCABULARY.map((v) => [v.action, v.category])) as Record<
    string,
    IdentityAuditCategory
  >
);

/**
 * THE resolver every emitter of an identity action reads its
 * `audit_logs.category` from.
 *
 * B2e-T1 review (R3 / BUG-02 / BUG-03). Two doors write
 * `IMPERSONATION_FORCE_ENDED` — `services/impersonation.service.ts`
 * (Users rail) and `routes/v1/admin/security/privileged.ts` (Admin >
 * Security > Privileged). The task reconciled the action string and left the
 * category split: one wrote `authentication`, the other `security`. Because
 * `GET /api/v1/audit?category=…` is an equality predicate, an operator
 * filtering to `security` saw exactly half the force-ends, with no hint the
 * other half existed. The same shape hid password resets and session
 * revocations from a `security` filter while the registry the audit UI reads
 * called them security events.
 *
 * An emitter that writes an action the vocabulary does not carry — the
 * hundreds of non-identity actions the same two helpers write — keeps its own
 * default, which is what `fallback` is for. Nothing is widened; one column
 * stops having two owners.
 *
 * @param action   the `audit_logs.action` about to be written
 * @param fallback the emitter's own category, used when the action is not an
 *                 identity-activity action
 */
export function resolveIdentityAuditCategory<TFallback extends string>(
  action: string,
  fallback: TFallback
): IdentityAuditCategory | TFallback {
  return USER_ACTIVITY_ACTION_TO_CATEGORY[action] ?? fallback;
}

/**
 * UI verb -> the actions that render as it, BY INVERSION.
 *
 * This is the half that used to be hand-written, and the half that carried
 * `session_terminated: ['SESSION_TERMINATED']` — a type whose action no
 * emitter wrote and the whitelist did not carry, so selecting it returned an
 * empty page forever. An inverted index cannot hold that.
 */
/** The derived verb list, needed above and exported below under its name. */
const USER_ACTIVITY_FILTER_TYPES_INTERNAL: readonly UserActivityFilterType[] = [
  ...new Set(VOCABULARY.map((v) => v.type)),
].sort() as readonly UserActivityFilterType[];

export const USER_ACTIVITY_TYPE_TO_ACTIONS: Readonly<
  Record<UserActivityFilterType, readonly UserActivityActionName[]>
> = Object.freeze(
  VOCABULARY.reduce(
    (acc, v) => {
      (acc[v.type] ??= []).push(v.action);
      return acc;
    },
    {} as Record<UserActivityFilterType, UserActivityActionName[]>
  )
) as Readonly<Record<UserActivityFilterType, readonly UserActivityActionName[]>>;

// ─── What history was written in (decisions.md 6, 2026-09-08) ────────────────

/**
 * RETIRED SPELLINGS, TRANSLATED AT READ TIME — NEVER REWRITTEN.
 *
 * Founder decision, decisions.md 6 (2026-09-08): *"Historical audit action
 * names are aliased at read time, never rewritten. The audit log is
 * hash-chained; rewriting rows to tidy their action strings breaks the chain
 * that proves nobody rewrote them. New rows use the one vocabulary from now
 * on; old rows are translated when read."*
 *
 * Every deployment's `audit_logs` holds rows in the spellings the emitters
 * used before B2d-T1 and B2e-T1 moved them. If the feed's predicate carried
 * only the new names, moving the emitters would have DELETED a tenant's
 * password, impersonation and MFA-reset history from the page that shows it —
 * the same disappearance the mismatch caused, in the other direction.
 *
 * This map is READ-ONLY, in both senses. It widens what the feed MATCHES; it
 * never widens what an emitter may WRITE. A retired name has no entry in the
 * audit registry and the vocabulary test fails if any route writes one, so
 * "two names for one action" stays impossible going forward while the rows
 * already on disk keep their meaning.
 *
 * An entry is deleted only when a retention window has removed the last row
 * that needs it — never to tidy the map.
 */
export const RETIRED_ACTION_ALIASES: Readonly<
  Record<string, UserActivityActionName>
> = Object.freeze({
  // B2d-T1 moved these two off the dotted admin convention.
  'user.password_changed': 'PASSWORD_CHANGED',
  'user.password_reset': 'PASSWORD_RESET',
  // B2e-T1 moved the impersonation service's three.
  impersonation_started: 'IMPERSONATION_STARTED',
  impersonation_ended: 'IMPERSONATION_ENDED',
  // The tenant door called a force-end "revoked"; the platform door spelt the
  // same act in the dotted convention. Both are one action now.
  impersonation_revoked: 'IMPERSONATION_FORCE_ENDED',
  'security.impersonation_force_ended': 'IMPERSONATION_FORCE_ENDED',
  // B2e-T1 moved the per-person MFA reset onto the name the feed carries.
  MFA_RESET_BY_ADMIN: 'MFA_RESET',
});

/**
 * Every action string the feed must MATCH: the vocabulary plus the retired
 * spellings history is written in. This is what the `action IN (...)`
 * predicate and the stats counts are built from — never
 * `USER_ACTIVITY_ACTIONS` alone, which would hide every pre-B2e row.
 */
export const USER_ACTIVITY_READ_ACTIONS: readonly string[] = Object.freeze([
  ...(USER_ACTIVITY_ACTIONS as readonly string[]),
  ...Object.keys(RETIRED_ACTION_ALIASES),
]);

/**
 * Audit row -> UI verb, INCLUDING the retired spellings.
 *
 * The projection reads this one, so a 2026-08 impersonation row renders as
 * "Impersonation started" rather than falling through to `other`.
 */
export const USER_ACTIVITY_READ_ACTION_TO_TYPE: Readonly<
  Record<string, UserActivityFilterType>
> = Object.freeze({
  ...USER_ACTIVITY_ACTION_TO_TYPE,
  ...Object.fromEntries(
    Object.entries(RETIRED_ACTION_ALIASES).map(([retired, canonical]) => [
      retired,
      USER_ACTIVITY_ACTION_TO_TYPE[canonical],
    ])
  ),
});

/**
 * UI verb -> every action that renders as it, retired spellings included.
 *
 * Filtering the feed to "password changed" has to reach the rows written
 * before the name changed, or the filter becomes a second, narrower feed.
 */
export const USER_ACTIVITY_TYPE_TO_READ_ACTIONS: Readonly<
  Record<UserActivityFilterType, readonly string[]>
> = Object.freeze(
  Object.fromEntries(
    USER_ACTIVITY_FILTER_TYPES_INTERNAL.map((type) => [
      type,
      [
        ...USER_ACTIVITY_TYPE_TO_ACTIONS[type],
        ...Object.entries(RETIRED_ACTION_ALIASES)
          .filter(([, canonical]) => USER_ACTIVITY_ACTION_TO_TYPE[canonical] === type)
          .map(([retired]) => retired),
      ],
    ])
  ) as unknown as Record<UserActivityFilterType, readonly string[]>
);

/**
 * The type filter's alphabet, derived and sorted. The API's query enum and the
 * page's dropdown both read this, so neither can offer a filter the feed
 * cannot answer.
 */
export const USER_ACTIVITY_FILTER_TYPES: readonly UserActivityFilterType[] =
  Object.freeze(USER_ACTIVITY_FILTER_TYPES_INTERNAL);

/** Zod enum over the derived alphabet — the API's `?type=` gate. */
export const userActivityFilterTypeSchema = z.enum(
  USER_ACTIVITY_FILTER_TYPES as unknown as [
    UserActivityFilterType,
    ...UserActivityFilterType[],
  ]
);

/**
 * What a RENDERED row's `type` may be — the filter alphabet plus the `other`
 * fallback. The two response schemas in `routes/v1/users.ts` used to write
 * this union out by hand, twice, and both copies carried `mfa_challenge`,
 * `mfa_enabled` and `mfa_disabled` long after it was clear no row would ever
 * arrive with them.
 */
export const USER_ACTIVITY_EVENT_TYPES: readonly UserActivityEventType[] =
  Object.freeze([...USER_ACTIVITY_FILTER_TYPES, 'other' as const]);

export const userActivityEventTypeSchema = z.enum(
  USER_ACTIVITY_EVENT_TYPES as unknown as [
    UserActivityEventType,
    ...UserActivityEventType[],
  ]
);

// ─── The numbers on the cards ────────────────────────────────────────────────

/**
 * The windows the stats endpoint will count over. `24h` is what the cards ask
 * for; the wider two exist because an operator investigating an incident asks
 * the same question about a longer stretch.
 */
export const USER_ACTIVITY_STAT_WINDOWS = ['24h', '7d', '30d'] as const;
export type UserActivityStatWindow = (typeof USER_ACTIVITY_STAT_WINDOWS)[number];
export const userActivityStatWindowSchema = z.enum(USER_ACTIVITY_STAT_WINDOWS);

/** Hours per window — the only place the windows are given a length. */
export const USER_ACTIVITY_STAT_WINDOW_HOURS: Readonly<
  Record<UserActivityStatWindow, number>
> = Object.freeze({ '24h': 24, '7d': 24 * 7, '30d': 24 * 30 });

/**
 * The impersonation card asks a different question from the other three — "has
 * anyone been inside an account this WEEK" — and has always been labelled 7d.
 * It keeps its own fixed window rather than following `?window=`, so the card
 * and the number cannot disagree.
 */
export const IMPERSONATION_STAT_WINDOW: UserActivityStatWindow = '7d';

/**
 * What `GET /users/activity/stats` answers, and what the cards render.
 *
 * Every number here is a COUNT OVER `audit_logs` taken with the same predicate
 * the feed itself uses (`USER_ACTIVITY_TYPE_TO_ACTIONS[type]`, tenant-scoped,
 * attributed rows only). The browser renders these; it derives none of them.
 * It used to accumulate all four in a `for` loop over the page it happened to
 * have loaded, so "Logins (24h)" meant "logins in the newest 50 rows", which
 * is a different number with the same label.
 */
export const userActivityStatsSchema = z.object({
  /** The window the first three counts were taken over. */
  window: userActivityStatWindowSchema,
  /** Inclusive ISO lower bound of that window, so the caller can show it. */
  windowStart: z.string(),
  /** `LOGIN_SUCCESS` rows in the window. */
  logins: z.number().int().nonnegative(),
  /** `LOGIN_FAILED` rows in the window (attributed ones — see the feed). */
  failedLogins: z.number().int().nonnegative(),
  /** `MFA_RESET` rows in the window. */
  mfaResets: z.number().int().nonnegative(),
  /** `IMPERSONATION_STARTED` rows in the fixed impersonation window. */
  impersonations: z.number().int().nonnegative(),
  /** That fixed window, named rather than assumed by the card's label. */
  impersonationWindow: userActivityStatWindowSchema,
  /** Inclusive ISO lower bound of the impersonation window. */
  impersonationWindowStart: z.string(),
});
export type UserActivityStats = z.infer<typeof userActivityStatsSchema>;

/**
 * Which activity TYPE each card counts. The card's click-through filter reads
 * this too, so the number and the list it drills into can never disagree about
 * what they are counting.
 */
export const USER_ACTIVITY_STAT_TYPES = Object.freeze({
  logins: 'login',
  failedLogins: 'login_failed',
  mfaResets: 'mfa_reset',
  impersonations: 'impersonation_started',
} as const satisfies Record<string, UserActivityFilterType>);
